import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Svg, { Circle, G, Text as SvgText } from 'react-native-svg';
import { useTheme } from '../../contexts/ThemeContext';
import { FONTS } from '../../constants/config';
import { getStatusColor } from './chartStyles';
import { formatPct } from '../../utils/formatPercentage';

// ============================================================
// Types
// ============================================================

interface DonutChartProps {
  value: number;
  total?: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  showPercentage?: boolean;
  showLabel?: boolean;
  label?: string;
  centerText?: string;
  animated?: boolean;
}

// ============================================================
// DonutChart Component
// ============================================================

export const DonutChart = ({
  value,
  total = 100,
  size = 120,
  strokeWidth = 12,
  color,
  showPercentage = true,
  showLabel = false,
  label = '',
  centerText,
  animated = false,
}: DonutChartProps) => {
  const { colors, isDark } = useTheme();

  // Calculate percentage
  const percentage = total > 0 ? Math.min((value / total) * 100, 100) : 0;

  // Determine color based on percentage if not provided
  const chartColor = color || getStatusColor(percentage);

  // SVG calculations
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  // Track color
  const trackColor = isDark ? '#374151' : '#E5E7EB';

  return (
    <View style={[styles.container, { width: size, height: size }]}>
      <Svg width={size} height={size}>
        {/* Background Circle (Track) */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress Circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={chartColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>

      {/* Center Content */}
      <View style={styles.centerContent}>
        {centerText ? (
          <Text
            style={[
              styles.centerText,
              { color: colors.textPrimary },
            ]}
            numberOfLines={2}
          >
            {centerText}
          </Text>
        ) : (
          <>
            {showPercentage && (
              <Text
                style={[
                  styles.percentageText,
                  { color: colors.textPrimary, fontSize: size * 0.17 },
                ]}
              >
                %{formatPct(percentage)}
              </Text>
            )}
            {showLabel && label && (
              <Text
                style={[
                  styles.labelText,
                  { color: colors.textMuted, fontSize: size * 0.09 },
                ]}
                numberOfLines={1}
              >
                {label}
              </Text>
            )}
          </>
        )}
      </View>
    </View>
  );
};

// ============================================================
// Mini Donut (for inline use)
// ============================================================

interface MiniDonutProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
}

export const MiniDonut = ({
  percentage,
  size = 32,
  strokeWidth = 4,
  color,
}: MiniDonutProps) => {
  const { colors, isDark } = useTheme();

  const chartColor = color || getStatusColor(percentage);
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (Math.min(percentage, 100) / 100) * circumference;
  const trackColor = isDark ? '#374151' : '#E5E7EB';

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={chartColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>
    </View>
  );
};

// ============================================================
// Donut with Percentage Inside
// ============================================================

interface DonutWithPercentageProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
  color?: string;
  fontSize?: number;
}

export const DonutWithPercentage = ({
  percentage,
  size = 48,
  strokeWidth = 5,
  color,
  fontSize,
}: DonutWithPercentageProps) => {
  const { colors, isDark } = useTheme();

  const chartColor = color || getStatusColor(percentage);
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const strokeDashoffset = circumference - (Math.min(percentage, 100) / 100) * circumference;
  const trackColor = isDark ? '#374151' : '#E5E7EB';
  const textSize = fontSize || size * 0.28;

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={trackColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={chartColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text
          style={{
            fontSize: textSize,
            fontFamily: FONTS.numbers,
            fontWeight: '500',
            color: colors.textPrimary,
          }}
        >
          %{formatPct(percentage)}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  centerContent: {
    position: 'absolute',
    alignItems: 'center',
    justifyContent: 'center',
  },
  percentageText: {
    fontFamily: FONTS.numbers,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  labelText: {
    fontFamily: FONTS.regular,
    textAlign: 'center',
    marginTop: 2,
  },
  centerText: {
    fontFamily: FONTS.medium,
    textAlign: 'center',
    paddingHorizontal: 8,
  },
});

export default DonutChart;
